import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Settings,
  Filter,
  FileText,
  Briefcase,
  Users,
  CreditCard,
  MessageSquare,
  AlertTriangle,
  Volume2,
  VolumeX,
  X
} from "lucide-react";

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  quote_sent: { icon: FileText, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-900/30" },
  quote_viewed: { icon: FileText, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-900/30" },
  quote_approved: { icon: Check, color: "text-green-600", bg: "bg-green-100 dark:bg-green-900/30" },
  quote_expired: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-900/30" },
  contract_sent: { icon: FileText, color: "text-purple-600", bg: "bg-purple-100 dark:bg-purple-900/30" },
  contract_viewed: { icon: FileText, color: "text-purple-600", bg: "bg-purple-100 dark:bg-purple-900/30" },
  contract_signed: { icon: Check, color: "text-green-600", bg: "bg-green-100 dark:bg-green-900/30" },
  invoice_sent: { icon: FileText, color: "text-cyan-600", bg: "bg-cyan-100 dark:bg-cyan-900/30" },
  invoice_viewed: { icon: FileText, color: "text-cyan-600", bg: "bg-cyan-100 dark:bg-cyan-900/30" },
  invoice_paid: { icon: CreditCard, color: "text-green-600", bg: "bg-green-100 dark:bg-green-900/30" },
  invoice_overdue: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-100 dark:bg-red-900/30" },
  job_created: { icon: Briefcase, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-900/30" },
  job_assigned: { icon: Briefcase, color: "text-indigo-600", bg: "bg-indigo-100 dark:bg-indigo-900/30" },
  job_started: { icon: Briefcase, color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-900/30" },
  job_completed: { icon: Check, color: "text-green-600", bg: "bg-green-100 dark:bg-green-900/30" },
  technician_joined: { icon: Users, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-900/30" },
  technician_removed: { icon: Users, color: "text-red-600", bg: "bg-red-100 dark:bg-red-900/30" },
  subscription_expiring: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-900/30" },
  subscription_expired: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-100 dark:bg-red-900/30" },
  subscription_renewed: { icon: Check, color: "text-green-600", bg: "bg-green-100 dark:bg-green-900/30" },
  system_announcement: { icon: MessageSquare, color: "text-slate-600", bg: "bg-muted" },
  // Change Order notifications
  change_order_pending_pricing: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-100 dark:bg-amber-900/30" },
  change_order_approved: { icon: Check, color: "text-green-600", bg: "bg-green-100 dark:bg-green-900/30" },
  change_order_rejected: { icon: AlertTriangle, color: "text-red-600", bg: "bg-red-100 dark:bg-red-900/30" },
  change_order_sent: { icon: FileText, color: "text-blue-600", bg: "bg-blue-100 dark:bg-blue-900/30" },
};

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "quotes", label: "Quotes" },
  { key: "contracts", label: "Contracts" },
  { key: "invoices", label: "Invoices" },
  { key: "jobs", label: "Jobs" },
  { key: "team", label: "Team" },
  { key: "subscription", label: "Subscription" },
];

// Storage keys for tracking shown notifications
const SHOWN_NOTIFS_KEY = "tg_shown_browser_notifs";
const MAX_SHOWN_NOTIFS = 100;

// Get IDs of notifications that have already triggered browser notifications
const getShownNotificationIds = () => {
  try {
    const stored = localStorage.getItem(SHOWN_NOTIFS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

// Save shown notification IDs (keep only most recent)
const addShownNotificationId = (id) => {
  try {
    const shown = getShownNotificationIds();
    shown.add(id);
    // Keep only most recent IDs to prevent localStorage bloat
    const arr = Array.from(shown).slice(-MAX_SHOWN_NOTIFS);
    localStorage.setItem(SHOWN_NOTIFS_KEY, JSON.stringify(arr));
  } catch {
    // Ignore storage errors
  }
};

// Simple notification sound
const createNotificationSound = () => {
  if (typeof window === "undefined") return null;
  try {
    const audio = new Audio("data:audio/wav;base64,UklGRlQFAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YTAFAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAeHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eICAgICAgICAgICAgICAgICA");
    audio.volume = 0.3;
    return audio;
  } catch {
    return null;
  }
};

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [category, setCategory] = useState("all");
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem("notif_sound") !== "false";
  });
  const [browserNotifsEnabled, setBrowserNotifsEnabled] = useState(() => {
    return localStorage.getItem("notif_browser") !== "false";
  });
  
  const lastPollTimestamp = useRef(null);
  const pollInterval = useRef(null);
  const notificationSound = useRef(createNotificationSound());
  const isInitialMount = useRef(true);

  // Fetch notifications (full list)
  const fetchNotifications = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (category !== "all") params.append("category", category);
      
      const response = await api.get(`/notifications?${params}`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      
      // Set initial timestamp to prevent showing old notifications as new
      if (!lastPollTimestamp.current && response.data.notifications?.length > 0) {
        lastPollTimestamp.current = response.data.notifications[0].created_at;
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, [category]);

  // Poll for new notifications (only truly new ones)
  const pollForNewNotifications = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (lastPollTimestamp.current) {
        params.append("since", lastPollTimestamp.current);
      }
      params.append("unread_only", "true");
      
      const response = await api.get(`/notifications/poll?${params}`);
      const newNotifs = response.data.notifications || [];
      
      // Update unread count
      setUnreadCount(response.data.unread_count || 0);
      
      // Update timestamp for next poll
      if (response.data.timestamp) {
        lastPollTimestamp.current = response.data.timestamp;
      }
      
      if (newNotifs.length === 0) return;
      
      // Get IDs we've already shown browser notifications for
      const shownIds = getShownNotificationIds();
      
      // Filter to only truly new, unread notifications we haven't shown
      const trulyNewNotifs = newNotifs.filter(n => 
        !n.read && !shownIds.has(n.notification_id)
      );
      
      if (trulyNewNotifs.length === 0) {
        // Still update the list but don't show browser notifications
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.notification_id));
          const toAdd = newNotifs.filter(n => !existingIds.has(n.notification_id));
          return toAdd.length > 0 ? [...toAdd, ...prev].slice(0, 50) : prev;
        });
        return;
      }
      
      // Play sound once for new notifications
      if (soundEnabled && notificationSound.current) {
        notificationSound.current.play().catch(() => {});
      }
      
      // Show browser notification for the most recent new one only
      if (browserNotifsEnabled && Notification.permission === "granted") {
        const latest = trulyNewNotifs[0];
        try {
          new Notification(latest.title || "SkilledShark", {
            body: latest.message,
            icon: "/favicon.ico",
            tag: latest.notification_id, // Prevents duplicates
            requireInteraction: false
          });
        } catch (e) {
          console.error("Browser notification error:", e);
        }
      }
      
      // Mark all truly new notifications as "shown" for browser notifications
      trulyNewNotifs.forEach(n => addShownNotificationId(n.notification_id));
      
      // Update notifications list
      setNotifications(prev => {
        const existingIds = new Set(prev.map(n => n.notification_id));
        const toAdd = newNotifs.filter(n => !existingIds.has(n.notification_id));
        return toAdd.length > 0 ? [...toAdd, ...prev].slice(0, 50) : prev;
      });
      
    } catch (error) {
      console.error("Polling error:", error);
    }
  }, [soundEnabled, browserNotifsEnabled]);

  // Initial fetch and setup polling
  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
      if (!isMounted) return;
      
      // Initial fetch
      await fetchNotifications();
      
      // Mark initial mount as complete to start showing browser notifications
      setTimeout(() => {
        isInitialMount.current = false;
      }, 2000);
    };
    
    init();
    
    // Start polling every 30 seconds
    pollInterval.current = setInterval(pollForNewNotifications, 30000);
    
    return () => {
      isMounted = false;
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [fetchNotifications, pollForNewNotifications]);

  // Re-fetch when category changes
  useEffect(() => {
    fetchNotifications();
  }, [category, fetchNotifications]);

  // Request browser notification permission
  useEffect(() => {
    if (browserNotifsEnabled && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, [browserNotifsEnabled]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  }, []);

  // Mark all as read when notification panel opens
  useEffect(() => {
    if (open && unreadCount > 0) {
      markAllAsRead();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, markAllAsRead]);

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      await api.delete("/notifications/clear-all?read_only=false");
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to clear notifications:", error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    
    // Get data from notification - check both nested data object and top-level fields
    const data = notification.data || {};
    const jobId = data.job_id || notification.job_id;
    const quoteId = data.quote_id || notification.quote_id;
    const contractId = data.contract_id || notification.contract_id;
    const invoiceId = data.invoice_id || notification.invoice_id;
    const changeOrderId = data.change_order_id || notification.change_order_id;
    const type = notification.type;
    
    // Close the popover first
    setOpen(false);
    
    // Navigate based on notification type and available data
    if (type?.includes('change_order') && jobId) {
      // Change order notifications - go directly to job detail
      navigate(`/jobs/${jobId}`);
    } else if (type === 'quote_approved' && jobId) {
      // Quote approved - go to job detail
      navigate(`/jobs/${jobId}`);
    } else if (type === 'contract_signed' && jobId) {
      // Contract signed - go to job detail
      navigate(`/jobs/${jobId}`);
    } else if (type?.includes('invoice') && invoiceId) {
      // Invoice notifications
      navigate(`/invoices?invoice=${invoiceId}`);
    } else if (jobId) {
      // Any notification with a job_id - navigate to that job
      navigate(`/jobs/${jobId}`);
    } else if (invoiceId) {
      navigate(`/invoices?invoice=${invoiceId}`);
    } else if (quoteId) {
      navigate(`/jobs?quote=${quoteId}`);
    } else if (contractId) {
      navigate(`/jobs?contract=${contractId}`);
    }
  };

  // Toggle sound
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem("notif_sound", newValue.toString());
  };

  // Toggle browser notifications
  const toggleBrowserNotifs = () => {
    const newValue = !browserNotifsEnabled;
    setBrowserNotifsEnabled(newValue);
    localStorage.setItem("notif_browser", newValue.toString());
    
    if (newValue && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  };

  // Format time ago
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationConfig = (type) => {
    return NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.system_announcement;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-red-500 hover:bg-red-500"
              data-testid="unread-badge"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[400px] p-0" 
        align="end"
        data-testid="notification-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={toggleSound}
              title={soundEnabled ? "Mute sounds" : "Enable sounds"}
            >
              {soundEnabled ? (
                <Volume2 className="h-4 w-4" />
              ) : (
                <VolumeX className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={toggleBrowserNotifs}
              title={browserNotifsEnabled ? "Disable desktop notifications" : "Enable desktop notifications"}
            >
              <Bell className={`h-4 w-4 ${browserNotifsEnabled ? "" : "text-muted-foreground"}`} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Filter className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {CATEGORIES.map(cat => (
                  <DropdownMenuItem
                    key={cat.key}
                    onClick={() => setCategory(cat.key)}
                    className={category === cat.key ? "bg-accent" : ""}
                  >
                    {cat.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={markAllAsRead}
              >
                <CheckCheck className="h-4 w-4 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[300px] text-muted-foreground">
              <Bell className="h-12 w-12 mb-3 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const config = getNotificationConfig(notification.type);
                const Icon = config.icon;
                
                return (
                  <div
                    key={notification.notification_id}
                    className={`px-4 py-3 cursor-pointer hover:bg-muted transition-colors relative group ${
                      !notification.read ? "bg-blue-50/50 dark:bg-blue-950/20" : ""
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-item-${notification.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`h-9 w-9 rounded-full ${config.bg} flex items-center justify-center shrink-0`}>
                        <Icon className={`h-4 w-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notification.read ? "font-medium" : ""}`}>
                            {notification.title || notification.message}
                          </p>
                          {!notification.read && (
                            <div className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />
                          )}
                        </div>
                        {notification.title && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                            {notification.message}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {timeAgo(notification.created_at)}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 absolute right-2 top-2"
                        onClick={(e) => deleteNotification(notification.notification_id, e)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-t bg-muted">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={clearAllNotifications}
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Clear all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => {
                navigate("/settings?tab=notifications");
                setOpen(false);
              }}
            >
              <Settings className="h-3 w-3 mr-1" />
              Settings
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationCenter;
